// @flow

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { sizes, fontFamily, colors, colorPalette } from '../../../styles/GlobalStyle';
import { StyledAutoComplete } from '../../../styles/PlacesStyle';
import { useDebounce } from '../hooks/useDebounce';
import config from '../../../config/app.config';

import { useDispatch, useSelector } from 'react-redux';
import {
  WS_SEARCH_PLACES_REQUEST,
  WS_CLEAR_SEARCH_PLACES_RESULTS,
  WS_GET_PLACE_DETAILS_REQUEST,
} from '../../../store/actions';
import type { RootState } from '../../../store/types';
import type { SuggestionType } from '../../../modules/shared/types';
import { API } from '../../../utils/APIUtils';

import { Icon } from '../../../modules/shared/components/Icon';
import TextField from '@material-ui/core/TextField';
// https://github.com/moroshko/autosuggest-highlight
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';

type InputPlacesProps = {|
  className?: string,
  name: string,
  label: string,
  id: string,
  invalid?: boolean,
  onChange?: Function,
  onPlaceChange?: (event: any) => void,
  onSelectOption?: (event: any) => void,
  type: 'address' | 'city' | 'country' | 'zip',
  defaultValue?: string,
  queryType?: string,
  clearError?: Function,
  readOnlyField?: boolean,
|};

const InputPlacesRaw = forwardRef<InputPlacesProps, HTMLInputElement>(
  (
    {
      className,
      name,
      label,
      id,
      invalid = false,
      onChange,
      onPlaceChange,
      onSelectOption,
      type,
      defaultValue,
      queryType,
      clearError,
      readOnlyField = false,
    },
    ref
  ) => {
    const innerRef = useRef(null);
    const [suggestions, setSuggestions] = useState<Array<SuggestionType>>([]);
    const [isHighlighted, setIsHighlighted] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [componentValue, setComponentValue] = useState('');
    const [focused, setFocused] = useState(false);

    const userRegistration = useSelector((state: RootState) => state.userRegistration);
    useImperativeHandle(ref, () => innerRef.current);

    const dispatch = useDispatch();
    const common = useSelector((state: RootState) => state.common);
    // Debounce request, 2s in local, 250ms in dev/prod
    const debouncedSearchTerm = useDebounce(inputValue, !config.hosted ? 250 : 250);

    useEffect(() => {
      if (onChange) {
        if (debouncedSearchTerm) {
          if (focused && inputValue && inputValue !== '' && common.jwt && !readOnlyField) {
            dispatch({
              type: WS_SEARCH_PLACES_REQUEST,
              payload: {
                method: API.SearchPlaces,
                params: {
                  query: inputValue,
                  type: queryType,
                  jwt: common.jwt,
                },
              },
            });
          }
        }
      }
    }, [debouncedSearchTerm, dispatch]);

    // get suggestions from store and place them in state
    useEffect(() => {
      if (
        userRegistration.searchPlaces &&
        userRegistration.searchPlaces.response &&
        inputValue !== ''
      ) {
        const { response } = userRegistration.searchPlaces;
        const suggestionsResponse = response;

        setSuggestions(suggestionsResponse);
      }
      // Reset suggestions state when store is empty
      if (userRegistration.searchPlaces && !userRegistration.searchPlaces.response) {
        setSuggestions([]);
      }
    }, [userRegistration.searchPlaces, userRegistration.searchPlaces.response]);

    // countryInputValue comes from parent
    // Used to autofill country value based on city
    useEffect(() => {
      if (defaultValue) {
        setInputValue(defaultValue);
      }
    }, [defaultValue]);

    const clearInput = useCallback(() => {
      if(!readOnlyField){
        setInputValue('');
        if (onChange) {
          onChange('');
          onPlaceChange && onPlaceChange('');
        }
      }
    }, [setInputValue]);

    const preventEnter = useCallback((event) => {
      if ((event.charCode || event.keyCode) === 13) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, []);

    const handleFocus = useCallback((event) => {
      if (!readOnlyField){
        setFocused(true);

        // https://stackoverflow.com/a/61973547
        event.target.setAttribute('autocomplete', 'new-password');
      }
    });

    const handleBlur = useCallback(() => {
      setFocused(false);

      // Reset suggestions to avoid autocomplete keeping results from previous field search
      if (suggestions.length > 0) {
        setSuggestions([]);
        dispatch({
          type: WS_CLEAR_SEARCH_PLACES_RESULTS,
        });
      }
    });

    const getPlaceDetails = (placeId: string) => {
      dispatch({
        type: WS_GET_PLACE_DETAILS_REQUEST,
        payload: {
          originField: name,
          method: API.GetPlaceDetails,
          params: {
            place_id: placeId,
            jwt: common.jwt,
          },
        },
      });
    };

    return (
      <span className={`${className || ''}${invalid ? ' invalid' : ''}${readOnlyField ? ' locked-place': ''}`}>
        <StyledAutoComplete
          inputValue={inputValue}
          onFocus={handleFocus}
          onHighlightChange={(event, option, reason) => {
            if (option !== undefined) {
              setIsHighlighted(true);
            } else {
              setIsHighlighted(false);
            }
          }}
          onBlur={handleBlur}
          onClose={() => {
            setIsHighlighted(false);
          }}
          onKeyDown={(event) => {
            if (isHighlighted) preventEnter(event);
          }}
          onChange={(event, value, reason) => {
            if (reason === 'select-option') {
              onPlaceChange && onPlaceChange(value);
              setComponentValue(value);
              if (onSelectOption) {
                onSelectOption();
              }
              getPlaceDetails(value.place_id);
            }
          }}
          id={`${id}`}
          disableClearable
          disablePortal={true}
          clearOnBlur={false}
          readOnly={readOnlyField}
          onInputChange={(event, value, reason) => {
            setInputValue(value);
            if (onSelectOption && (reason === 'select-option' || reason === 'reset')) {
              onSelectOption();
            }
            // componentValue contains all data, whereas value contains only inputValue
            // Needed in order to autocomplete department when city is set again with previous value
            if (onPlaceChange && (reason === 'select-option' || reason === 'reset')) {
              onPlaceChange(componentValue);
            }
            if (onPlaceChange && reason !== 'select-option' && reason !== 'reset') {
              onPlaceChange(value);
            }
          }}
          options={suggestions}
          filterOptions={(x) => x}
          getOptionLabel={(option) => (option.locale_names && option.locale_names[0]) || ''}
          // Need to pass empty string instead of undefined to keep component as controlled
          renderOption={(option, { inputValue }) => {
            const optionByQueryType = option.locale_names && option.locale_names[0];
            const matches = match(optionByQueryType, inputValue);
            const parts = parse(optionByQueryType, matches);

            return (
              <div>
                {parts.map((part, index) => (
                  <span key={index} style={{ color: part.highlight ? colors.white : '' }}>
                    {part.text}
                  </span>
                ))}
                <span className="suggestion-details">{option.details}</span>
              </div>
            );
          }}
          renderInput={(params) => {
            return (
              <>
                <TextField
                  ref={innerRef}
                  {...params}
                  label={label}
                  name={name}
                  InputProps={{ ...params.InputProps, readOnly: readOnlyField }}
                />
                {inputValue && inputValue.length !== 0 && !readOnlyField && (
                  <div
                    className="input-clear"
                    onClick={clearInput}
                    role="button"
                    tabIndex={-1}
                    onKeyUp={() => {}}
                  >
                    <Icon name="close" />
                  </div>
                )}

                {readOnlyField && (
                  <div className="input-read-only" tabIndex={-1}>
                    <Icon name="lock" />
                  </div>
                )}
              </>
            );
          }}
          readOnlyField={readOnlyField}
        />
      </span>
    );
  }
);

export const InputPlaces: StyledComponent<InputPlacesProps, {}, typeof InputPlacesRaw> = styled(
  InputPlacesRaw
)`
  position: relative;
  display: inline-block;
  width: 100%;
  border-radius: ${sizes.borderRadius};
  font-weight: ${fontFamily.primary.black.fontWeight};

  .suggestion-details {
    display: block;
    font-size: 12px;
    font-weight: 600;
    line-height: 15px;
  }

  .input-clear {
    position: absolute;
    right: 0;
    bottom: 20px;
    color: ${colors.yellow};
    cursor: pointer;
    outline: 0;
    transition: 300ms all ease-out;
    animation: scaleIn 0.3s ease-out;
    svg {
      width: 18px;
      height: 18px;
    }
    &:hover {
      color: #fff;
    }
  }

  .locked-place {
    .MuiAutocomplete-root .MuiFormControl-root .MuiFormLabel-root {
      color: ${(props) => (props.readOnlyField ? colors.grey : '#fff')};
    }

    .MuiAutocomplete-input {
      color: ${(props) => (props.readOnlyField ? colors.grey : '#fff')};
    }

    .MuiInput-underline {
      border-bottom: 2px solid ${(props) => (props.readOnlyField ? colors.grey : '#fff')};
    }
  }

  .input-read-only {
    position: absolute;
    right: 0;
    bottom: 20px;
    color: ${colors.yellow};
    outline: 0;
    svg {
      width: 18px;
      height: 18px;
    }
  }

  &.invalid {
    .MuiAutocomplete-root .MuiFormControl-root .MuiFormLabel-root {
      color: ${colorPalette.danger.active};
    }

    .MuiAutocomplete-input {
      color: ${colorPalette.danger.active};
    }

    .MuiInput-underline {
      border-bottom: 2px solid ${colorPalette.danger.active};
    }
  }
`;
